/**
 * army-info-panel.ts — v0M8 (2026-07-23).
 *
 * Strategic-map left-hand side DOM overlay showing the selected Army's
 * stats + roster + formation preview. Visible only when the player has
 * selected one of their own armies (per `selection.selectedArmyId` in
 * `main.ts`).
 *
 * Contents (top → bottom):
 *   1. Header — commander name / rank, close button
 *   2. Faction + position + movement target
 *   3. Movement speed (cells/tick — slowest cohort proxy)
 *   4. Vision range — stubbed '—' until v0M5
 *   5. Supplies — stubbed '—' until v0M5+
 *   6. Average morale (mean across live cohorts)
 *   7. Average stamina (mean across live cohorts)
 *   8. Cohort roster — one row per cohort with type + Alive/Lost figure
 *      counts
 *   9. Formation preview canvas — reuses v0M4.4 renderer, overlaying
 *      cohort-type labels at slot positions per the placement algorithm
 *
 * Diff-based updates — DOM structure built ONCE in mount; sync mutates
 * text nodes in place.
 */

import { injectMenuStyles } from './menu-styles';
import { getFactionById } from '../../data/factions';
import { loadCohorts } from '../../data/cohorts';
import { loadFormationTemplates, slotsByTag, cohortPrimaryTag } from '../../data/formation-templates';
import type { FormationSlot } from '../../data/formation-templates';
import { loadCommanderLevels, commanderMaxLevel, cumulativeXpForLevel } from '../../data/commander-levels';
import { loadDials, getDialNumber } from '../../data/dials';
import { ATTACHMENT_UNLOCK_STRUCTURE } from '../../sim/structure-effects';
import { ATTACHMENT_TYPES } from '../../sim/state';
import type { Command } from '../../sim/commands';
import type { ArmyId, FactionId, GameState, WorldStructureKind } from '../../sim/state';
import type { Army, Cohort } from '../../sim/state';

// v0M7.5 (2026-07-25) — WorldStructureKind → display label + knowledge
// prereq. Kept in sync with `KNOWLEDGE_BY_KIND` in apply-commands.ts
// (which gates the actual sim-side command). Order is the display
// order in the Build Orders section.
const BUILD_ORDER_KINDS: readonly {
  readonly kind: WorldStructureKind;
  readonly label: string;
  readonly knowledge: string;
}[] = [
  { kind: 'road',       label: 'Build Road',        knowledge: 'Road Construction' },
  { kind: 'watchtower', label: 'Build Watch Tower', knowledge: 'Watch Tower Construction' },
  { kind: 'bridge',     label: 'Build Bridge',      knowledge: 'Bridge Construction' },
  { kind: 'fort',       label: 'Build Fort',        knowledge: 'Fort Construction' },
];

// v0M10.2 (2026-07-24) — Settlement-ZoC dial for the attachments section.
// Loaded once at module init.
const AIP_DIALS = loadDials();
const AIP_SETTLEMENT_ZOC_RADIUS = getDialNumber(AIP_DIALS, 'settlement_zoc_radius');

const COHORTS_SPEC = loadCohorts();
const FORMATION_LIBRARY = loadFormationTemplates();
// v0M9.6 (2026-07-24) — Commander level table for the XP progress bar +
// unlocks list under the header. Loaded once at module init.
const COMMANDER_LEVELS = loadCommanderLevels();
const COMMANDER_MAX_LEVEL = commanderMaxLevel(COMMANDER_LEVELS);

export interface ArmyInfoPanelHandle {
  /** Refresh the display; hides the panel when `selectedArmyId` is null. */
  sync(state: GameState, selectedArmyId: ArmyId | null): void;
  destroy(): void;
}

export function mountArmyInfoPanel(
  root: HTMLElement,
  playerFactionId: FactionId | null,
  callbacks?: {
    onClose?: () => void;
    onIssue?: (cmd: Command) => void;
    /** v0M7.5 (2026-07-25) — Fired when the player clicks a Build X
     *  button. Main.ts enters location-picker mode; next LMB on the
     *  map dispatches `set_army_build_order` against `armyId`. */
    onStartBuildOrder?: (armyId: ArmyId, kind: WorldStructureKind) => void;
    /** MFix12.5b (2026-07-27) — Fired when the player clicks "Transfer
     *  Cohort". Main.ts opens the cohort-transfer modal against `armyId`. */
    onOpenCohortTransfer?: (armyId: ArmyId) => void;
  },
): ArmyInfoPanelHandle {
  injectMenuStyles();

  const panel = document.createElement('div');
  panel.className = 'su-army-info-panel';
  panel.style.position = 'fixed';
  panel.style.top = '90px';
  panel.style.left = '12px';
  panel.style.width = '280px';
  panel.style.maxHeight = 'calc(100vh - 120px)';
  panel.style.overflowY = 'auto';
  panel.style.background = 'rgba(20, 16, 10, 0.94)';
  panel.style.border = '1px solid #6a5a36';
  panel.style.borderRadius = '4px';
  panel.style.padding = '12px 14px';
  panel.style.color = '#f5eacf';
  panel.style.fontFamily = '"Trajan Pro", "Cinzel", "Times New Roman", serif';
  panel.style.fontSize = '12px';
  panel.style.zIndex = '900';
  panel.style.display = 'none';
  panel.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.5)';

  // MFix8.4 (2026-07-24) — header with title + close (X) button on the
  // right. Header now a flex row rather than a plain bold div.
  const header = document.createElement('div');
  header.style.display = 'flex';
  header.style.justifyContent = 'space-between';
  header.style.alignItems = 'center';
  header.style.paddingBottom = '6px';
  header.style.borderBottom = '1px solid #4a3820';
  header.style.marginBottom = '8px';
  const headerTitle = document.createElement('span');
  headerTitle.style.fontWeight = 'bold';
  headerTitle.style.fontSize = '14px';
  header.appendChild(headerTitle);
  const closeBtn = document.createElement('button');
  closeBtn.textContent = '×';
  closeBtn.setAttribute('aria-label', 'Close army info');
  closeBtn.style.background = 'transparent';
  closeBtn.style.color = '#f5eacf';
  closeBtn.style.border = 'none';
  closeBtn.style.fontSize = '18px';
  closeBtn.style.lineHeight = '1';
  closeBtn.style.padding = '0 4px';
  closeBtn.style.cursor = 'pointer';
  closeBtn.style.opacity = '0.75';
  closeBtn.onmouseenter = () => { closeBtn.style.opacity = '1'; };
  closeBtn.onmouseleave = () => { closeBtn.style.opacity = '0.75'; };
  closeBtn.addEventListener('click', () => {
    panel.style.display = 'none';
    lastArmyIdCached = null;
    callbacks?.onClose?.();
  });
  header.appendChild(closeBtn);
  panel.appendChild(header);

  // v0M9.6 (2026-07-24) — Commander XP + unlocks subsection. Sits between
  // the header (which already carries "Commander L{level}") and the stat
  // rows. Structure built once; sync mutates text/width in place.
  const commanderBlock = document.createElement('div');
  commanderBlock.style.padding = '4px 6px 6px 6px';
  commanderBlock.style.margin = '0 0 8px 0';
  commanderBlock.style.background = 'rgba(196, 168, 116, 0.05)';
  commanderBlock.style.border = '1px solid #4a3820';
  commanderBlock.style.borderRadius = '3px';
  const xpLabel = document.createElement('div');
  xpLabel.style.fontSize = '10px';
  xpLabel.style.opacity = '0.85';
  xpLabel.style.marginBottom = '3px';
  xpLabel.textContent = 'XP —';
  commanderBlock.appendChild(xpLabel);
  const xpBarOuter = document.createElement('div');
  xpBarOuter.style.height = '5px';
  xpBarOuter.style.background = 'rgba(255, 255, 255, 0.10)';
  xpBarOuter.style.borderRadius = '2px';
  xpBarOuter.style.overflow = 'hidden';
  const xpBarFill = document.createElement('div');
  xpBarFill.style.height = '100%';
  xpBarFill.style.background = '#f4d47a';
  xpBarFill.style.width = '0%';
  xpBarFill.style.transition = 'width 200ms linear';
  xpBarOuter.appendChild(xpBarFill);
  commanderBlock.appendChild(xpBarOuter);
  // Unlocks list — populated per-frame from levels 1..currentLevel where
  // the CommanderUnlocks row has a non-empty `unlocks` string.
  const unlocksList = document.createElement('div');
  unlocksList.style.fontSize = '10px';
  unlocksList.style.opacity = '0.85';
  unlocksList.style.marginTop = '4px';
  commanderBlock.appendChild(unlocksList);
  panel.appendChild(commanderBlock);
  let lastUnlocksKey = '';

  const rows: Record<string, HTMLDivElement> = {};
  const STAT_KEYS = [
    'Faction', 'Position', 'Moving to', 'Speed', 'Vision', 'Supplies', 'Morale', 'Stamina',
  ] as const;
  for (const key of STAT_KEYS) {
    const row = document.createElement('div');
    row.style.display = 'flex';
    row.style.justifyContent = 'space-between';
    row.style.padding = '2px 0';
    const label = document.createElement('span');
    label.style.opacity = '0.75';
    label.textContent = key;
    const value = document.createElement('span');
    value.style.fontWeight = 'bold';
    value.dataset.role = 'value';
    row.appendChild(label);
    row.appendChild(value);
    panel.appendChild(row);
    rows[key] = row;
  }

  const rosterLabel = document.createElement('div');
  rosterLabel.style.marginTop = '10px';
  rosterLabel.style.paddingTop = '6px';
  rosterLabel.style.borderTop = '1px solid #4a3820';
  rosterLabel.style.opacity = '0.75';
  rosterLabel.style.marginBottom = '4px';
  rosterLabel.textContent = 'Cohorts';
  panel.appendChild(rosterLabel);

  const rosterList = document.createElement('div');
  panel.appendChild(rosterList);

  // v0M10.2 (2026-07-24) — Attachments block. Shows attached items and
  // an "Add Attachment" button when: a slot is free AND the army is in
  // a friendly settlement's ZoC AND that settlement has ≥1 unlocking
  // structure built. Clicking the button opens an inline picker of
  // available types; picking one dispatches add_army_attachment.
  const attachmentsLabel = document.createElement('div');
  attachmentsLabel.style.marginTop = '10px';
  attachmentsLabel.style.paddingTop = '6px';
  attachmentsLabel.style.borderTop = '1px solid #4a3820';
  attachmentsLabel.style.opacity = '0.75';
  attachmentsLabel.style.marginBottom = '4px';
  attachmentsLabel.textContent = 'Attachments';
  panel.appendChild(attachmentsLabel);

  const attachmentsList = document.createElement('div');
  attachmentsList.style.fontSize = '11px';
  attachmentsList.style.marginBottom = '4px';
  panel.appendChild(attachmentsList);

  const addAttachmentBtn = document.createElement('button');
  addAttachmentBtn.textContent = '+ Add Attachment';
  addAttachmentBtn.style.width = '100%';
  addAttachmentBtn.style.padding = '4px';
  addAttachmentBtn.style.background = '#3a2c18';
  addAttachmentBtn.style.color = '#f5eacf';
  addAttachmentBtn.style.border = '1px solid #6a5238';
  addAttachmentBtn.style.fontFamily = 'inherit';
  addAttachmentBtn.style.fontSize = '11px';
  addAttachmentBtn.style.cursor = 'pointer';
  addAttachmentBtn.style.display = 'none';
  panel.appendChild(addAttachmentBtn);

  // Picker container — hidden by default; shown when Add Attachment
  // clicked. Populated per-frame with buttons for available types.
  const attachmentPicker = document.createElement('div');
  attachmentPicker.style.marginTop = '4px';
  attachmentPicker.style.display = 'none';
  panel.appendChild(attachmentPicker);
  let pickerOpen = false;
  addAttachmentBtn.addEventListener('click', () => {
    pickerOpen = !pickerOpen;
    attachmentPicker.style.display = pickerOpen ? '' : 'none';
    addAttachmentBtn.textContent = pickerOpen ? 'Cancel' : '+ Add Attachment';
  });

  // v0M7.5 (2026-07-25) — Build Orders section per MAP.md §9.
  // Visible only when the selected Army carries the `Engineer`
  // attachment. Individual per-kind buttons are visible when the
  // owning faction has researched the corresponding Construction
  // knowledge. Buttons + label rebuilt only on structural changes;
  // in-place enable/disable per frame.
  const buildOrdersLabel = document.createElement('div');
  buildOrdersLabel.style.marginTop = '10px';
  buildOrdersLabel.style.paddingTop = '6px';
  buildOrdersLabel.style.borderTop = '1px solid #4a3820';
  buildOrdersLabel.style.opacity = '0.75';
  buildOrdersLabel.style.marginBottom = '4px';
  buildOrdersLabel.textContent = 'Build Orders';
  buildOrdersLabel.style.display = 'none';
  panel.appendChild(buildOrdersLabel);

  const buildOrdersActive = document.createElement('div');
  buildOrdersActive.style.fontSize = '11px';
  buildOrdersActive.style.marginBottom = '4px';
  buildOrdersActive.style.opacity = '0.9';
  buildOrdersActive.style.display = 'none';
  panel.appendChild(buildOrdersActive);

  const buildOrdersContainer = document.createElement('div');
  buildOrdersContainer.style.display = 'none';
  buildOrdersContainer.style.marginBottom = '4px';
  panel.appendChild(buildOrdersContainer);

  // One button per kind, keyed by kind. Built once; enable/disable per frame.
  const buildOrderButtons = new Map<WorldStructureKind, HTMLButtonElement>();
  for (const entry of BUILD_ORDER_KINDS) {
    const btn = document.createElement('button');
    btn.textContent = entry.label;
    btn.dataset.buildKind = entry.kind;
    btn.style.display = 'block';
    btn.style.width = '100%';
    btn.style.padding = '4px';
    btn.style.marginBottom = '2px';
    btn.style.background = '#3a2c18';
    btn.style.color = '#f5eacf';
    btn.style.border = '1px solid #6a5238';
    btn.style.fontFamily = 'inherit';
    btn.style.fontSize = '11px';
    btn.style.cursor = 'pointer';
    btn.addEventListener('click', () => {
      if (currentArmyId === null) return;
      if (!callbacks?.onStartBuildOrder) return;
      callbacks.onStartBuildOrder(currentArmyId, entry.kind);
    });
    buildOrdersContainer.appendChild(btn);
    buildOrderButtons.set(entry.kind, btn);
  }

  // Cancel button — shown only when an army has an active build order.
  const buildOrderCancelBtn = document.createElement('button');
  buildOrderCancelBtn.textContent = 'Cancel Build';
  buildOrderCancelBtn.style.width = '100%';
  buildOrderCancelBtn.style.padding = '4px';
  buildOrderCancelBtn.style.marginTop = '4px';
  buildOrderCancelBtn.style.background = '#4a3020';
  buildOrderCancelBtn.style.color = '#f5eacf';
  buildOrderCancelBtn.style.border = '1px solid #6a5238';
  buildOrderCancelBtn.style.fontFamily = 'inherit';
  buildOrderCancelBtn.style.fontSize = '11px';
  buildOrderCancelBtn.style.cursor = 'pointer';
  buildOrderCancelBtn.style.display = 'none';
  buildOrderCancelBtn.addEventListener('click', () => {
    if (currentArmyId === null) return;
    if (!callbacks?.onIssue || playerFactionId === null) return;
    callbacks.onIssue({
      type: 'cancel_army_build_order',
      tick: 0,
      playerFactionId,
      armyId: currentArmyId,
    });
  });
  panel.appendChild(buildOrderCancelBtn);

  // v0M7.4d+siege (2026-07-26) — Assault Fort button. Surfaces when
  // the selected Army is Chebyshev-1 adjacent to (or on) an enemy
  // Fort, and isn't already in a battle. Click dispatches
  // `assault_fort`. Spec per MAP.md §9.2: direct-assault, no siege
  // state. Battle carries `siegeFortId` + resolves via
  // `resolveFortAssaultConclusion` in `battle-conclusion.ts` — on
  // attacker victory the Fort ownership flips + garrison destroyed.
  //
  // If MULTIPLE enemy Forts are within adjacency (rare — need two
  // Forts on Chebyshev-1 cells around the army), the panel picks the
  // first one iteration-order matches; a picker UI is a follow-up.
  const assaultFortBtn = document.createElement('button');
  assaultFortBtn.textContent = 'Assault Fort';
  assaultFortBtn.style.display = 'none';
  assaultFortBtn.style.width = '100%';
  assaultFortBtn.style.padding = '4px';
  assaultFortBtn.style.marginTop = '4px';
  assaultFortBtn.style.background = '#5a2820';
  assaultFortBtn.style.color = '#f5eacf';
  assaultFortBtn.style.border = '1px solid #8a3a28';
  assaultFortBtn.style.fontFamily = 'inherit';
  assaultFortBtn.style.fontSize = '11px';
  assaultFortBtn.style.cursor = 'pointer';
  // fortAssaultTargetId is the Fort id the button will assault. Set
  // by sync() when it detects an adjacent enemy Fort.
  let fortAssaultTargetId: import('../../sim/state').WorldStructureId | null = null;
  assaultFortBtn.addEventListener('click', () => {
    if (currentArmyId === null || fortAssaultTargetId === null) return;
    if (!callbacks?.onIssue || playerFactionId === null) return;
    callbacks.onIssue({
      type: 'assault_fort',
      tick: 0,
      playerFactionId,
      armyId: currentArmyId,
      fortId: fortAssaultTargetId,
    });
  });
  panel.appendChild(assaultFortBtn);

  // MFix12.5b (2026-07-27) — Transfer Cohort button. Surfaces when at
  // least one other own-faction army is within TRANSFER_RANGE
  // (zoc_radius / 2) Euclidean of the currently-selected army. Click
  // opens the cohort-transfer modal via the onOpenCohortTransfer
  // callback (main.ts wires it to selection state).
  const transferCohortBtn = document.createElement('button');
  transferCohortBtn.textContent = 'Transfer Cohort';
  transferCohortBtn.style.display = 'none';
  transferCohortBtn.style.width = '100%';
  transferCohortBtn.style.padding = '4px';
  transferCohortBtn.style.marginTop = '4px';
  transferCohortBtn.style.background = '#3a4028';
  transferCohortBtn.style.color = '#f5eacf';
  transferCohortBtn.style.border = '1px solid #5a6238';
  transferCohortBtn.style.fontFamily = 'inherit';
  transferCohortBtn.style.fontSize = '11px';
  transferCohortBtn.style.cursor = 'pointer';
  transferCohortBtn.addEventListener('click', () => {
    if (currentArmyId === null) return;
    callbacks?.onOpenCohortTransfer?.(currentArmyId);
  });
  panel.appendChild(transferCohortBtn);

  let currentArmyId: ArmyId | null = null;

  const previewLabel = document.createElement('div');
  previewLabel.style.marginTop = '10px';
  previewLabel.style.paddingTop = '6px';
  previewLabel.style.borderTop = '1px solid #4a3820';
  previewLabel.style.opacity = '0.75';
  previewLabel.style.marginBottom = '4px';
  previewLabel.textContent = 'Formation preview';
  panel.appendChild(previewLabel);

  const previewSelect = document.createElement('select');
  previewSelect.style.width = '100%';
  previewSelect.style.marginBottom = '4px';
  previewSelect.style.background = '#3a2c18';
  previewSelect.style.color = '#f5eacf';
  previewSelect.style.border = '1px solid #6a5238';
  previewSelect.style.fontFamily = 'inherit';
  previewSelect.style.padding = '3px';
  for (const name of Object.keys(FORMATION_LIBRARY).sort()) {
    const opt = document.createElement('option');
    opt.value = name;
    opt.textContent = humaniseName(name);
    previewSelect.appendChild(opt);
  }
  panel.appendChild(previewSelect);

  const previewCanvas = document.createElement('canvas');
  // Session-28 (2026-07-24): formation slots always sit in rows 10-19
  // (own side's half of the battlefield). Top 10 rows are always blank,
  // so trim the canvas to just show rows 10-19. Also gained a tag-colour
  // legend below the canvas so the coding is self-explanatory.
  previewCanvas.width = 240;
  previewCanvas.height = 120;
  previewCanvas.style.border = '1px solid #4a3d24';
  previewCanvas.style.background = 'rgba(20, 16, 10, 0.5)';
  previewCanvas.style.imageRendering = 'pixelated';
  panel.appendChild(previewCanvas);

  // Legend for the tag-colour coding (Session-28).
  const legend = document.createElement('div');
  legend.style.display = 'flex';
  legend.style.flexWrap = 'wrap';
  legend.style.gap = '6px 10px';
  legend.style.marginTop = '4px';
  legend.style.fontSize = '10px';
  legend.style.opacity = '0.85';
  const LEGEND_ITEMS: readonly { readonly tag: string; readonly label: string; readonly color: string }[] = [
    { tag: 'A', label: 'Any',     color: 'rgb(200, 200, 200)' },
    { tag: 'M', label: 'Melee',   color: 'rgb(196, 116, 116)' },
    { tag: 'R', label: 'Ranged',  color: 'rgb(116, 168, 212)' },
    { tag: 'C', label: 'Cavalry', color: 'rgb(220, 200, 116)' },
    { tag: 'S', label: 'Siege',   color: 'rgb(140, 196, 140)' },
  ];
  for (const item of LEGEND_ITEMS) {
    const entry = document.createElement('span');
    entry.style.display = 'inline-flex';
    entry.style.alignItems = 'center';
    entry.style.gap = '4px';
    const swatch = document.createElement('span');
    swatch.style.display = 'inline-block';
    swatch.style.width = '10px';
    swatch.style.height = '10px';
    swatch.style.background = item.color;
    swatch.style.border = '1px solid rgba(74, 61, 36, 0.7)';
    const label = document.createElement('span');
    label.textContent = item.label;
    entry.appendChild(swatch);
    entry.appendChild(label);
    legend.appendChild(entry);
  }
  panel.appendChild(legend);

  root.appendChild(panel);

  let lastArmyIdCached: ArmyId | null = null;
  let lastRosterKey = '';

  previewSelect.addEventListener('change', () => {
    if (lastArmyIdCached !== null) drawFormationPreview(lastArmyIdCached);
  });

  function sync(state: GameState, selectedArmyId: ArmyId | null): void {
    if (playerFactionId === null || selectedArmyId === null) {
      panel.style.display = 'none';
      lastArmyIdCached = null;
      return;
    }
    const army = state.armies.get(selectedArmyId);
    if (!army || army.ownerFactionId !== playerFactionId) {
      panel.style.display = 'none';
      lastArmyIdCached = null;
      return;
    }
    panel.style.display = 'block';
    lastArmyIdCached = selectedArmyId;

    // Header — commander + army id.
    const commander = state.commanders.get(army.commanderId);
    const commanderLevel = commander?.level ?? 1;
    const commanderXp = commander?.xp ?? 0;
    // MFix8.4: mutate the title span only, not the whole header (which
    // now contains the close X button as a sibling).
    headerTitle.textContent = `Army #${army.id as unknown as number} — Commander L${commanderLevel}`;

    // v0M9.6 (2026-07-24) — Commander XP progress + unlocks list.
    // v0M10.6 (2026-07-24) — commander.xp is CUMULATIVE career XP
    // (never resets on level-up per battle-conclusion.ts). Progress
    // toward next level = xp - cumulative-to-current-level; threshold =
    // xpForNextLevel at current level. Reads xlsx-authoritative
    // CommanderUnlocks per Michael's design lock.
    const currentLevelSpec = COMMANDER_LEVELS.find((l) => l.level === commanderLevel);
    if (commanderLevel >= COMMANDER_MAX_LEVEL) {
      xpLabel.textContent = `XP ${Math.floor(commanderXp).toLocaleString()} — MAX LEVEL`;
      xpBarFill.style.width = '100%';
    } else if (currentLevelSpec && currentLevelSpec.xpForNextLevel > 0) {
      const cumulativeToCurrent = cumulativeXpForLevel(commanderLevel, COMMANDER_LEVELS);
      const progress = Math.max(0, commanderXp - cumulativeToCurrent);
      const threshold = currentLevelSpec.xpForNextLevel;
      const pct = Math.max(0, Math.min(100, (progress / threshold) * 100));
      xpBarFill.style.width = `${pct.toFixed(1)}%`;
      xpLabel.textContent = `XP ${Math.floor(progress).toLocaleString()} / ${threshold.toLocaleString()} → L${commanderLevel + 1}`;
    } else {
      xpLabel.textContent = `XP ${Math.floor(commanderXp).toLocaleString()}`;
      xpBarFill.style.width = '0%';
    }
    // Unlocks list — rebuild only when the level (and thus the visible
    // set) changes. Cheap either way (≤10 entries) but keeps DOM stable.
    const unlocksKey = `L${commanderLevel}`;
    if (unlocksKey !== lastUnlocksKey) {
      unlocksList.innerHTML = '';
      const earned: { level: number; unlocks: string }[] = [];
      for (const spec of COMMANDER_LEVELS) {
        if (spec.level > commanderLevel) break;
        if (spec.unlocks) earned.push({ level: spec.level, unlocks: spec.unlocks });
      }
      if (earned.length === 0) {
        const empty = document.createElement('div');
        empty.style.opacity = '0.55';
        empty.style.fontStyle = 'italic';
        empty.textContent = 'No unlocks yet';
        unlocksList.appendChild(empty);
      } else {
        for (const e of earned) {
          const row = document.createElement('div');
          const badge = document.createElement('span');
          badge.textContent = `L${e.level}`;
          badge.style.color = '#c4a874';
          badge.style.marginRight = '6px';
          const text = document.createElement('span');
          text.textContent = e.unlocks;
          row.appendChild(badge);
          row.appendChild(text);
          unlocksList.appendChild(row);
        }
      }
      lastUnlocksKey = unlocksKey;
    }

    // Stat rows.
    const cohorts: Cohort[] = [];
    for (const cid of army.cohortIds) {
      const c = state.cohorts.get(cid);
      if (c) cohorts.push(c);
    }
    const facVal = rows['Faction']!.querySelector<HTMLSpanElement>('[data-role="value"]')!;
    let facName = '—';
    try { facName = getFactionById(army.ownerFactionId as unknown as number).name; } catch { /* neutral */ }
    facVal.textContent = facName;
    setValue(rows['Position']!, `(${army.position.x.toFixed(1)}, ${army.position.y.toFixed(1)})`);
    setValue(rows['Moving to']!, army.movementGoal
      ? `(${army.movementGoal.x}, ${army.movementGoal.y})`
      : 'idle');
    setValue(rows['Speed']!, `${slowestCohortSpeed(cohorts).toFixed(2)} c/tick`);
    setValue(rows['Vision']!, '—');   // v0M5 stub
    // v0M10.1 (2026-07-24) — live supply readout. Coalesce for pre-v0M10
    // armies that may not have the fields populated yet.
    {
      const cur = Math.floor(army.supplies ?? 0);
      const cap = Math.floor(army.supplyCap ?? 1200);
      setValue(rows['Supplies']!, `${cur.toLocaleString()} / ${cap.toLocaleString()}`);
    }
    setValue(rows['Morale']!, `${Math.round(mean(cohorts.map((c) => c.morale)))} / 100`);
    setValue(rows['Stamina']!, `${Math.round(mean(cohorts.map((c) => c.stamina)))} / 100`);

    // v0M10.2 (2026-07-24) — attachments block refresh.
    {
      const attached = army.attachments ?? [];
      const cap = army.attachmentCap ?? 1;
      // Populate the existing-attachments list. Rebuild each frame — the
      // list is tiny (≤ cap = 1 at v1) so DOM cost is trivial.
      attachmentsList.innerHTML = '';
      if (attached.length === 0) {
        const empty = document.createElement('div');
        empty.style.opacity = '0.55';
        empty.style.fontStyle = 'italic';
        empty.textContent = 'None';
        attachmentsList.appendChild(empty);
      } else {
        for (const a of attached) {
          const row = document.createElement('div');
          row.style.padding = '1px 0';
          row.textContent = `• ${a}`;
          attachmentsList.appendChild(row);
        }
      }
      // Compute which attachment types are unlockable RIGHT NOW: army
      // sits in a friendly settlement's ZoC AND that settlement has the
      // gating structure. Show the "+ Add Attachment" button when a slot
      // is free and ≥1 type is unlockable.
      const availableTypes: string[] = [];
      if (attached.length < cap && army.currentBattleId === null) {
        for (const type of ATTACHMENT_TYPES) {
          const requiredStructure = ATTACHMENT_UNLOCK_STRUCTURE[type];
          if (!requiredStructure) continue;
          // Skip types the army already has (no duplicates at v1).
          if (attached.includes(type)) continue;
          // Scan settlements for a friendly-ZoC one that has the structure.
          for (const settlement of state.settlements.values()) {
            const prov = state.provinces.get(settlement.provinceId);
            if (!prov) continue;
            if (prov.ownerFactionId !== playerFactionId) continue;
            const dx = Math.abs(army.position.x - settlement.cell.x);
            const dy = Math.abs(army.position.y - settlement.cell.y);
            if (Math.max(dx, dy) > AIP_SETTLEMENT_ZOC_RADIUS) continue;
            if (!prov.structures.some((s) => s.name === requiredStructure)) continue;
            availableTypes.push(type);
            break; // Found one qualifying settlement — no need to check more.
          }
        }
      }
      const canAdd = availableTypes.length > 0;
      addAttachmentBtn.style.display = canAdd ? '' : 'none';
      // If picker was open but no longer eligible (army moved out of
      // ZoC, slot filled, etc.), auto-collapse.
      if (!canAdd && pickerOpen) {
        pickerOpen = false;
        attachmentPicker.style.display = 'none';
        addAttachmentBtn.textContent = '+ Add Attachment';
      }
      // Rebuild the picker each frame from availableTypes. Tiny list
      // (≤ 3) so full rebuild is cheap; keeps handlers current.
      if (pickerOpen) {
        attachmentPicker.innerHTML = '';
        for (const type of availableTypes) {
          const btn = document.createElement('button');
          btn.textContent = type;
          btn.style.display = 'block';
          btn.style.width = '100%';
          btn.style.padding = '3px';
          btn.style.marginTop = '2px';
          btn.style.background = '#2a2010';
          btn.style.color = '#f5eacf';
          btn.style.border = '1px solid #4a3820';
          btn.style.fontFamily = 'inherit';
          btn.style.fontSize = '11px';
          btn.style.cursor = 'pointer';
          const capturedType = type;
          btn.addEventListener('click', () => {
            if (!callbacks?.onIssue || playerFactionId === null) return;
            callbacks.onIssue({
              type: 'add_army_attachment',
              tick: state.tick,
              playerFactionId,
              armyId: army.id,
              attachmentType: capturedType,
            });
            // Collapse the picker on selection.
            pickerOpen = false;
            attachmentPicker.style.display = 'none';
            addAttachmentBtn.textContent = '+ Add Attachment';
          });
          attachmentPicker.appendChild(btn);
        }
      }
    }

    // v0M7.5 (2026-07-25) — Build Orders section refresh.
    // Visible when the Army carries the Engineer attachment. Per-kind
    // buttons enabled when the owning faction has researched the
    // corresponding Construction knowledge AND the Army has no active
    // build order + is not in battle. Cancel button visible when the
    // Army has an active build order.
    currentArmyId = selectedArmyId;
    {
      const attached = army.attachments ?? [];
      const hasEngineer = attached.includes('Engineer');
      const inBattle = army.currentBattleId !== null;
      const hasActiveOrder = army.buildOrder != null;
      const showSection = hasEngineer;
      buildOrdersLabel.style.display = showSection ? '' : 'none';
      buildOrdersContainer.style.display = showSection ? '' : 'none';
      buildOrderCancelBtn.style.display = showSection && hasActiveOrder ? '' : 'none';
      buildOrdersActive.style.display = showSection && hasActiveOrder ? '' : 'none';

      if (showSection) {
        const faction = state.factions.get(playerFactionId);
        const researched = faction?.researchedKnowledge ?? [];
        for (const entry of BUILD_ORDER_KINDS) {
          const btn = buildOrderButtons.get(entry.kind)!;
          const knowledgeOk = researched.includes(entry.knowledge);
          // Hide entirely when knowledge not researched — reduces noise
          // in the panel; player learns availability by researching.
          btn.style.display = knowledgeOk ? 'block' : 'none';
          btn.disabled = !knowledgeOk || inBattle || hasActiveOrder;
        }
        if (hasActiveOrder && army.buildOrder) {
          const kind = army.buildOrder.kind;
          const t = army.buildOrder.targetCell;
          const days = army.buildOrder.progressDays;
          buildOrdersActive.textContent =
            `In progress: Build ${kind} @ (${t.x}, ${t.y}) — ${days.toFixed(1)}d elapsed`;
        }
      }
    }

    // v0M7.4d+siege (2026-07-26) — Assault Fort button visibility.
    // Scan state.worldStructures for enemy Forts within Chebyshev-1
    // of the army; if any, arm the button with the first such Fort's
    // id. Silent when no adjacent enemy Fort exists.
    {
      fortAssaultTargetId = null;
      const inBattle = army.currentBattleId !== null;
      if (!inBattle && state.worldStructures) {
        const ax = Math.floor(army.position.x);
        const ay = Math.floor(army.position.y);
        for (const ws of state.worldStructures.values()) {
          if (ws.kind !== 'fort') continue;
          if (ws.ownerFactionId === army.ownerFactionId) continue;
          if (ws.ownerFactionId === null) continue;
          const dx = Math.abs(ax - ws.cell.x);
          const dy = Math.abs(ay - ws.cell.y);
          if (Math.max(dx, dy) <= 1) {
            fortAssaultTargetId = ws.id;
            break;
          }
        }
      }
      assaultFortBtn.style.display = fortAssaultTargetId !== null ? '' : 'none';
    }

    // MFix12.5b (2026-07-27) — Transfer Cohort button visibility.
    // Enabled when at least one other own-faction army is within
    // zoc_radius / 2 Euclidean of this army AND neither is in battle.
    // AI dial from a hardcoded fallback since we don't want another
    // loadDials round in this panel — matches the sim's constant.
    {
      let hasTransferTarget = false;
      const inBattle = army.currentBattleId !== null;
      if (!inBattle && army.ownerFactionId === playerFactionId) {
        // Hardcoded zoc_radius/2 to avoid another dial import; matches
        // AC_ZOC_RADIUS_FOR_TRANSFER in apply-commands.ts (currently
        // zoc_radius = 6 → range 3). If Michael changes the dial, this
        // stays visually generous — sim always has the final say.
        const RANGE = 3;
        for (const other of state.armies.values()) {
          if (other.id === army.id) continue;
          if (other.ownerFactionId !== army.ownerFactionId) continue;
          if (other.currentBattleId !== null) continue;
          const dx = army.position.x - other.position.x;
          const dy = army.position.y - other.position.y;
          if (Math.hypot(dx, dy) <= RANGE) {
            hasTransferTarget = true;
            break;
          }
        }
      }
      // Also require at least one transferable cohort in this army
      // (non-garrison) so the button doesn't tempt the player with an
      // empty modal.
      const hasTransferable = army.cohortIds.some((cid) => {
        const c = state.cohorts.get(cid);
        if (!c) return false;
        if (c.settlementId !== null) return false;
        if (c.worldStructureId !== null && c.worldStructureId !== undefined) return false;
        return true;
      });
      transferCohortBtn.style.display = (hasTransferTarget && hasTransferable) ? '' : 'none';
    }

    // Roster — rebuild only if cohort id list changes.
    const rosterKey = cohorts.map((c) => `${c.id}:${c.aliveFigureCount}`).join(',');
    if (rosterKey !== lastRosterKey) {
      rosterList.innerHTML = '';
      for (const c of cohorts) {
        const spec = COHORTS_SPEC[c.type];
        const max = spec?.figureCount ?? 60;
        const lost = Math.max(0, max - c.aliveFigureCount);
        const row = document.createElement('div');
        row.style.display = 'flex';
        row.style.justifyContent = 'space-between';
        row.style.padding = '1px 0';
        const name = document.createElement('span');
        name.style.opacity = '0.9';
        name.textContent = c.type;
        const count = document.createElement('span');
        count.style.color = lost === 0 ? '#a5c07a' : lost > max / 2 ? '#c47474' : '#d4a874';
        // Session-28 (2026-07-24): renamed "N A / M L" (Alive/Lost — ambiguous
        // to playtesters) → "N alive / M lost" (spelled out).
        count.textContent = `${c.aliveFigureCount} alive / ${lost} lost`;
        row.appendChild(name);
        row.appendChild(count);
        rosterList.appendChild(row);
      }
      lastRosterKey = rosterKey;
    }

    drawFormationPreview(selectedArmyId);
  }

  function drawFormationPreview(armyId: ArmyId): void {
    const ctx = previewCanvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
    const templateName = previewSelect.value;
    const template = FORMATION_LIBRARY[templateName];
    if (!template) return;

    // Session-28 (2026-07-24): show only battlefield rows 10-19 (own half).
    // Rows 0-9 are the opponent's half and always blank in a formation
    // template — trimming them frees vertical real-estate. Canvas is
    // sized 240×120 = 20 cols × 10 rows at cellPx=12.
    const ROW_OFFSET = 10;
    const cellPx = previewCanvas.width / 20;
    const rowToY = (r: number): number => (r - ROW_OFFSET) * cellPx;

    // Faint grid.
    ctx.strokeStyle = 'rgba(74, 61, 36, 0.35)';
    ctx.lineWidth = 1;
    for (let i = 1; i < 20; i++) {
      ctx.beginPath();
      ctx.moveTo(i * cellPx, 0);
      ctx.lineTo(i * cellPx, previewCanvas.height);
      ctx.stroke();
    }
    for (let j = 1; j < 10; j++) {
      ctx.beginPath();
      ctx.moveTo(0, j * cellPx);
      ctx.lineTo(previewCanvas.width, j * cellPx);
      ctx.stroke();
    }
    // Empty slot fill (background) by tag.
    const TAG_BG: Record<string, string> = {
      A: 'rgba(160, 160, 160, 0.30)',
      M: 'rgba(196, 116, 116, 0.30)',
      R: 'rgba(116, 168, 212, 0.30)',
      C: 'rgba(220, 200, 116, 0.30)',
      S: 'rgba(140, 196, 140, 0.30)',
    };
    for (const slot of template.slots) {
      if (slot.row < ROW_OFFSET) continue; // Defensive — should never hit.
      ctx.fillStyle = TAG_BG[slot.tag] ?? 'rgba(200, 200, 200, 0.3)';
      ctx.fillRect(slot.col * cellPx, rowToY(slot.row), cellPx, cellPx);
    }

    // Overlay: run the placement algorithm using this army's cohorts.
    // Fill same-tag slots first, then A slots, ignoring passability (this
    // is a preview under the "all tiles passable" assumption).
    const army = getCurrentArmy(armyId);
    if (!army) return;
    const cohorts: Cohort[] = [];
    for (const cid of army.cohortIds) {
      const c = getCurrentCohort(cid);
      if (c) cohorts.push(c);
    }
    cohorts.sort((a, b) => (a.id as unknown as number) - (b.id as unknown as number));

    // Precompute slot lists per tag.
    const tagSlots: Record<string, FormationSlot[]> = {
      A: slotsByTag(template, 'A'),
      M: slotsByTag(template, 'M'),
      R: slotsByTag(template, 'R'),
      C: slotsByTag(template, 'C'),
      S: slotsByTag(template, 'S'),
    };
    const filled = new Set<string>();

    ctx.font = 'bold 8px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (const cohort of cohorts) {
      const spec = COHORTS_SPEC[cohort.type];
      const isCavalry = (spec?.moveSpeedCombat ?? 1) >= 2;
      const tag = cohortPrimaryTag({ tags: spec?.tags ?? [], isCavalry });
      const candidateSlots: FormationSlot[] = [
        ...tagSlots[tag] ?? [],
        ...(tag !== 'A' ? (tagSlots.A ?? []) : []),
      ];
      let placed: FormationSlot | null = null;
      for (const slot of candidateSlots) {
        const key = `${slot.tag}${slot.priority}`;
        if (filled.has(key)) continue;
        filled.add(key);
        placed = slot;
        break;
      }
      if (placed) {
        // Solid coloured square at placement + short cohort-type initial.
        const TAG_FG: Record<string, string> = {
          A: 'rgb(200, 200, 200)',
          M: 'rgb(196, 116, 116)',
          R: 'rgb(116, 168, 212)',
          C: 'rgb(220, 200, 116)',
          S: 'rgb(140, 196, 140)',
        };
        ctx.fillStyle = TAG_FG[placed.tag] ?? 'rgb(200, 200, 200)';
        ctx.fillRect(placed.col * cellPx, rowToY(placed.row), cellPx, cellPx);
        ctx.fillStyle = '#1a140a';
        const initial = cohort.type.charAt(0);
        ctx.fillText(initial, placed.col * cellPx + cellPx / 2, rowToY(placed.row) + cellPx / 2);
      }
    }
  }

  // Cached state pointers for the preview's drop-down change handler.
  let currentState: GameState | null = null;
  function getCurrentArmy(armyId: ArmyId): Army | undefined {
    return currentState?.armies.get(armyId);
  }
  function getCurrentCohort(cohortId: import('../../sim/state').CohortId): Cohort | undefined {
    return currentState?.cohorts.get(cohortId);
  }

  const outerSync = sync;
  return {
    sync(state, selectedArmyId) {
      currentState = state;
      outerSync(state, selectedArmyId);
    },
    destroy() {
      panel.remove();
    },
  };
}

function setValue(row: HTMLDivElement, text: string): void {
  const v = row.querySelector<HTMLSpanElement>('[data-role="value"]');
  if (v && v.textContent !== text) v.textContent = text;
}

function mean(vals: number[]): number {
  if (vals.length === 0) return 0;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function slowestCohortSpeed(cohorts: Cohort[]): number {
  let slowest = Infinity;
  for (const c of cohorts) {
    const spec = COHORTS_SPEC[c.type];
    const s = spec?.moveSpeedCombat ?? 1;
    if (s < slowest) slowest = s;
  }
  return Number.isFinite(slowest) ? slowest : 1;
}

function humaniseName(name: string): string {
  const stripped = name.startsWith('Formation') ? name.slice('Formation'.length) : name;
  return stripped.replace(/([a-z])([A-Z])/g, '$1 $2');
}
